// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL, authFetch, fetchTeacherAttendanceProgressionSummary } from '../../services/api';
import StandardsProgressionDropdowns from '../Admin/StandardsProgressionDropdowns';

// Set to true once Attendance subject is ready for production.
const SHOW_ATTENDANCE = false;

export default function StandardsProgression({ taskCreation = false, onTaskContinue }: any) {
  const { token, logout } = useAuth();
  const { state } = useLocation();
  const navigate = useNavigate();
  const prefill = state?.prefill || {};
  // Restore dropdown state if user came back from grade-entry
  const restoredState = state?.progressionState || {};
  const [standards, setStandards] = useState<any>([]);
  const [attendanceMetrics, setAttendanceMetrics] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [, setFilters] = useState({ gradeLevels: [], subject: '', domain: '', standards: [] });

  useEffect(() => {
    async function fetchStandards() {
      const endpointCandidates = [
        `${API_URL}/api/progression/standards-progression-data?ts=${Date.now()}`,
        `${API_URL}/api/teacher/standards-progression-data?ts=${Date.now()}`,
      ];

      try {
        setLoading(true);
        setError('');

        let standardsData = null;
        let lastStatus = null;
        let networkFailureCount = 0;

        for (const endpoint of endpointCandidates) {
          let res;
          try {
            res = await authFetch(endpoint, {
              headers: { 'Content-Type': 'application/json' },
            });
          } catch (fetchErr: any) {
            networkFailureCount += 1;
            console.error('[Teacher StandardsProgression] Network error while loading standards endpoint:', endpoint, fetchErr);
            continue;
          }

          lastStatus = res.status;

          if (res.status === 401) {
            logout();
            navigate('/login');
            return;
          }

          if (!res.ok) {
            continue;
          }

          const text = await res.text();
          let data;
          try {
            data = JSON.parse(text);
          } catch (_e: any) {
            console.error('[Teacher StandardsProgression] Response not JSON. Raw text:', text);
            continue;
          }

          const candidateRows = Array.isArray(data)
            ? data
            : Array.isArray(data?.standards)
              ? data.standards
              : Array.isArray(data?.data)
                ? data.data
                : null;

          if (Array.isArray(candidateRows)) {
            standardsData = candidateRows;
            break;
          }
        }

        if (!Array.isArray(standardsData)) {
          if (networkFailureCount === endpointCandidates.length) {
            throw new Error('Failed to fetch standards data from the API. Please retry and check backend connectivity.');
          }
          throw new Error(lastStatus ? `Failed to fetch standards progression data (${lastStatus})` : 'Could not load standards progression data.');
        }
        setStandards(standardsData);

        try {
          if (SHOW_ATTENDANCE) {
            const attendanceSummary = await fetchTeacherAttendanceProgressionSummary();
            setAttendanceMetrics(Array.isArray(attendanceSummary?.items) ? attendanceSummary.items : []);
          }
        } catch (_attendanceErr: any) {
          setAttendanceMetrics([]);
        }

      } catch (err: any) {
        setError(err.message || 'Could not load standards progression data.');
      } finally {
        setLoading(false);
      }
    }

    if (!token) {
      setError('Teacher session not found. Please log in again.');
      setLoading(false);
      return;
    }

    fetchStandards();
  }, [token, logout, navigate]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 1152, background: 'white', borderRadius: 14, boxShadow: '0 2px 8px #0001', border: '1px solid #e0e7ef', padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <StandardsProgressionDropdowns
            data={standards}
            attendanceMetrics={attendanceMetrics}
            onChange={setFilters}
            showProgress={false}
            title={taskCreation ? 'Create New Learning Task' : 'Enter New Data Points'}
            continueLabel={taskCreation ? 'Continue to Create New Task →' : undefined}
            showScorePresetHint={!taskCreation}
            initialGrade={restoredState.grade || prefill.grade || ''}
            initialSubject={restoredState.subject || prefill.subject || ''}
            initialDomain={restoredState.domain || ''}
            initialStrand={restoredState.strand || ''}
            initialExpandedDomains={restoredState.expandedDomains}
            initialExpandedStrands={restoredState.expandedStrands}
            focusedStandardCode={restoredState.publishedStandardCode || restoredState.focusedStandardCode || ''}
            compact={true}
            enableContinueFlow={true}
            onContinue={(selections, options = {}) => {
              const selArray = Array.isArray(selections) ? selections : [selections];
              if (taskCreation) {
                onTaskContinue(selArray);
                return;
              }
              const firstSel = selArray[0] || {};
              const nextProgressionState = {
                grade: firstSel.grade || restoredState.grade || prefill.grade || '',
                subject: firstSel.subject || '',
                domain: firstSel.domain || '',
                strand: firstSel.strandDescription || firstSel.strand || '',
                strandDescription: firstSel.strandDescription || '',
                ...options.progressionState,
              };
              navigate('/teacher/grade-entry', {
                state: {
                  selections: selArray,
                  initialScoreColor: options.initialScoreColor || '',
                  progressionState: nextProgressionState,
                  returnTo: {
                    pathname: '/teacher/standards-progression',
                    state: {
                      ...state,
                      progressionState: nextProgressionState,
                    },
                    label: '← Back to Enter New Data Points',
                  },
                },
              });
            }}
          />
        </div>
        {loading && <div style={{ color: '#6b7280', fontSize: 15 }}>Loading...</div>}
        {error && standards.length === 0 && <div style={{ color: '#b91c1c', fontWeight: 600, fontSize: 15 }}>{error}</div>}
        {!loading && !error && standards.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: 15 }}>{taskCreation ? 'No standards are available for creating a learning task.' : 'No data point entry data is available for this teacher view.'}</div>
        )}
      </div>
    </div>
  );
}
